import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { useSLocations } from "../../hooks/queries";
import { useCreateLocation, useUpdateLocation } from "../../hooks/mutation";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const HotelNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [hotel, setHotel] = useState(null);
  const [search, setSearch] = useState(null);
  const { data } = useSLocations(isEdit, search, page);
  const [hotelsData, setHotelsData] = useState();
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (data) {
      // Filter only hotels (LocationTypeID = 1)
      const hotelData = data.data.filter((item) => item.LocationTypeID === 1);
      setHotelsData(hotelData);
      setTotalCount(hotelData.length);
    }
  }, [data]);

  const [companies, setCompanies] = useState([]);
  const [hotelLocationType, setHotelLocationType] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      LocationTypeID: "",
      Code: "",
      Description: "",
      MinutesFromAirport: "",
    },
  });

  useEffect(() => {
    if (hotel) {
      reset({
        CompanyID: hotel.CompanyID,
        LocationTypeID: hotel.LocationTypeID,
        Code: hotel.Code,
        Description: hotel.Description,
        MinutesFromAirport: hotel.MinutesFromAirport,
      });
    }
  }, [hotel, reset]);

  useEffect(() => {
    const fetchHotelData = async () => {
      const { data: Companies, error: cError } = await supabase
        .from("Companies")
        .select("CompanyID, Name");

      // Find hotel location type (LocationTypeID = 1)
      const { data: LocationTypes, error: LError } = await supabase
        .from("LocationTypes")
        .select("LocationTypeID, Code, Description")
        .eq("LocationTypeID", 1);

      if (cError) {
        setCompanies([]);
      } else if (LError) {
        setHotelLocationType(null);
      } else {
        setCompanies(Companies);
        setHotelLocationType(LocationTypes[0]);
        reset({
          CompanyID: Companies[0] ? Companies[0].CompanyID : "",
          LocationTypeID: LocationTypes[0]
            ? LocationTypes[0].LocationTypeID
            : "",
          Code: "",
          Description: "",
          MinutesFromAirport: "",
        });
      }
    };
    fetchHotelData();
  }, [reset]);

  useEffect(() => {
    const fetchHotels = async () => {
      const { data: Hotels, error: hError } = await supabase
        .from("Locations")
        .select(
          `
          LocationID,
          created_at,
          Code,
          Description,
          MinutesFromAirport,
          Company:CompanyID (
            CompanyID,
            Name
          ),
          LocationType:LocationTypeID (
            LocationTypeID,
            Code
          )
        `
        )
        .eq("LocationTypeID", 1)
        .order("created_at", { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (hError) {
        setHotelsData([]);
      } else {
        setHotelsData(Hotels || []);
      }
    };
    fetchHotels();
  }, [page]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => old - 1);
  }, []);

  const onSuccess = useCallback((_data) => {
    toast.success("Success");
  }, []);
  const onError = useCallback((err) => {
    console.log("error", err);
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      LocationTypeID: hotelLocationType ? hotelLocationType.LocationTypeID : "",
      Code: "",
      Description: "",
      MinutesFromAirport: "",
    });
    setIsEdit(true);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setHotel();
  };

  const { mutate: createLocation, isPending: isCreating } = useCreateLocation(
    onSuccess,
    onError
  );

  const { mutate: updateLocation, isPending: isUpdating } = useUpdateLocation(
    onSuccess,
    onError
  );

  const OnCreate = useCallback(
    (data) => {
      if (hotel) {
        updateLocation({ ...data, ID: hotel.LocationID });
      } else {
        createLocation(data);
      }
    },
    [hotel, createLocation, updateLocation]
  );

  const onEdit = (hotelData) => {
    setHotel(hotelData);
    setIsEdit(true);
  };

  const onDelete = async (hotelId) => {
    if (window.confirm("Are you sure you want to delete this hotel?")) {
      const { error } = await supabase
        .from("Locations")
        .delete()
        .eq("LocationID", hotelId);

      if (error) {
        toast.error("Error deleting hotel");
      } else {
        toast.success("Hotel deleted successfully");
        // Refresh the list
        window.location.reload();
      }
    }
  };

  return (
    <div>
      <div className="flex">
        <h5 className="mb-0">Hotel Management</h5>
        <button
          className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
          onClick={onAddNew}
        >
          <span>Add New Hotel</span>
        </button>
      </div>

      <SearchBar setSearch={setSearch} OnSearch={() => {}} />

      {isEdit && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
          <h6 className="mb-3">Hotel Details</h6>
          <form onSubmit={handleSubmit(OnCreate)}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Company
                </label>
                <Controller
                  control={control}
                  name="CompanyID"
                  rules={{ required: "Company is required" }}
                  render={({ field: { value, onChange } }) => (
                    <select
                      className="custom-input rounded-lg w-full"
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option
                          key={company.CompanyID}
                          value={company.CompanyID}
                        >
                          {company.Name}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.CompanyID && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.CompanyID.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Hotel Code
                </label>
                <Controller
                  control={control}
                  name="Code"
                  rules={{ required: "Hotel code is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., HILTON001"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.Code && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <Controller
                  control={control}
                  name="Description"
                  rules={{ required: "Description is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., Hilton Los Angeles Airport"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.Description && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Description.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Minutes from Airport
                </label>
                <Controller
                  control={control}
                  name="MinutesFromAirport"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="0"
                      type="number"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="justify-between p-2 pr-20 pl-20 rounded-lg flex items-center custom-blue-button border-none"
              >
                <span>{hotel ? "Update" : "Create"}</span>
              </button>
              <button
                type="button"
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-4">
        <table className="table-auto w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 border">ID</th>
              <th className="px-4 py-2 border">Code</th>
              <th className="px-4 py-2 border">Description</th>
              <th className="px-4 py-2 border">Company</th>
              <th className="px-4 py-2 border">Minutes from Airport</th>
              <th className="px-4 py-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {hotelsData?.map((hotel) => (
              <tr key={hotel.LocationID} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{hotel.LocationID}</td>
                <td className="px-4 py-2 border">{hotel.Code}</td>
                <td className="px-4 py-2 border">{hotel.Description}</td>
                <td className="px-4 py-2 border">{hotel.Company?.Name}</td>
                <td className="px-4 py-2 border">{hotel.MinutesFromAirport}</td>
                <td className="px-4 py-2 border">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(hotel)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => onDelete(hotel.LocationID)}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination-container justify-start mt-4">
          <button
            onClick={onPrevPage}
            disabled={page === 1}
            className="pagination-button"
          >
            <FaAngleLeft className="pagination-icon" />
          </button>

          <span className="mx-4">
            Page {page} of {Math.ceil(totalCount / PAGE_SIZE)}
          </span>

          <button
            onClick={onNextPage}
            disabled={page * PAGE_SIZE >= totalCount}
            className="pagination-button"
          >
            <FaAngleRight className="pagination-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Code",
      which: "contains",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"LocationID"}>LocationID</option>
                <option value={"Name"}>Company</option>
                <option value={"Code"}>Code</option>
                <option value={"Description"}>Description</option>
                <option value={"MinutesFromAirport"}>MinutesFromAirport</option>
              </select>
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <p className="mb-0">Which:</p>
        <Controller
          control={searchControl}
          name="which"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"equals"}>Equals</option>
              <option value={"contains"}>Contains</option>
              <option value={"starts with"}>Starts With</option>
              <option value={"ends with"}>Ends With</option>
            </select>
          )}
        />
      </div>

      <div className="flex items-center gap-2">
        <Controller
          control={searchControl}
          name="text"
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              className="custom-input rounded-lg"
              value={value}
              onChange={onChange}
              placeholder="Search text"
            />
          )}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSearchSubmit((data) => {
            setSearch(data);
            OnSearch();
          })}
          className="justify-between rounded-lg flex items-center custom-search-button border-none"
        >
          <span>Search</span>
        </button>
        <button
          onClick={() => {
            searchReset();
            setSearch(null);
          }}
          className="justify-between rounded-lg flex items-center custom-reset-button"
        >
          <span>Reset</span>
        </button>
      </div>
    </div>
  );
};

export default HotelNew;
